// REACT
import React, { useEffect, useState } from "react";

// MATERIAL-UI
import {
  Grid,
  Paper,
  Typography,
  Modal,
  Button,
  OutlinedInput,
  Alert,
  Container,
  Stack,
} from "@mui/material";

// COMPONENTS
import ModalHeader from "../Withdraw/ModalHeader";
import Preloader from "src/utils/Preloader";
import DepositAddressError from "../DepositAddressError";
import CopyAddress from "./CopyAddress";

// ASSETS
import QRCode from "qrcode";
import { QrmodalProps } from "src/components/wallets/common/types/qrModalTypes";
import { QrModalStyles } from "src/components/wallets/common/styles/QrModal.styles";
import Clipboard from "src/icons/ClipboardIcon";
import { SubmitButton } from "src/components/forms/SubmitButton";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { ProcessDepositApiArg } from "src/store/features/api/generated";
import GenericMessageModal from "../Withdraw/GenericMessageModal";
import { setSessionCookie, getSessionItem } from "src/utils/SessionManagement";
import clsx from "clsx";

// Form validation
import * as Yup from "yup";
import { trackEvent } from "src/Amplitude";
import api from "src/store/features/api";
import DepositMinimumDisclaimer from "../../common/DepositMinimumDisclaimer";

const COPY_ADDRESS = "Copy Address";
type ComponentStateType = "normal" | "success" | "fail";
const QRModalDesktop = (props: QrmodalProps) => {
  const classes = QrModalStyles();
  const [qrCode, setQRCode] = useState("");
  const [message, setMessage] = useState("");
  const [copy, setCopy] = useState(false);
  const [afterSubmissionState, setAfterSubmissionState] = useState<ComponentStateType>("normal");

  const modalTitleData = {
    code: props.token.code,
    icon: props.token.logoUrl,
    name: props.token.name,
    onClose: props.onClose,
  };

  const depositable = props.token.depositable;
  const [blured, setBlured] = useState(!depositable);
  const instructionsMessage =
    "PLEASE NOTE: To successfully submit this deposit you will need to enter your transaction ID and the unit amount deposited.";
  const [postDepositCrypto] = api.useProcessDepositMutation();

  const submitDeposit = async (values: any) => {
    let zurichid = values.txId + values.amount;
    if (getSessionItem("trx") === zurichid) {
      setAfterSubmissionState("fail");
      setMessage("This transaction has already been created, check your transactions page");
      return;
    }
    setSessionCookie("trx", zurichid);

    const query: ProcessDepositApiArg = {
      processDepositDto: {
        token: props.token.code,
        address_id: props.token.id || "",
        tx_id: values.txId,
        amount: +values.amount,
        zurich_id: zurichid,
      },
    };
    await postDepositCrypto(query)
      .unwrap()
      .then((r) => {
        if (r) {
          setAfterSubmissionState("success");
          setMessage(
            "Thanks, you've successfully submitted your deposit. This deposit will soon appear on your transactions page.",
          );
        } else {
          setAfterSubmissionState("fail");
          setMessage("Something went wrong");
        }
      })
      .catch((e) => {
        setAfterSubmissionState("fail");
        setMessage("Something went wrong");
      });
  };

  const validationSchema = Yup.object().shape({
    txId: Yup.string().max(100).required("Transaction ID is required"),
    amount: Yup.number().positive().min(0).required("Amount is required"),
  });

  const generateQR = async (text: string | undefined) => {
    if (text) {
      try {
        const res = await QRCode.toDataURL(text);
        setQRCode(res);
      } catch (err) {
        console.warn(err);
      }
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(props.token.address ? props.token.address : "");
    setCopy((prev) => !prev);
    setTimeout(() => {
      setCopy((prev) => !prev);
    }, 1500);
  };

  const toggleVisibility = () => {
    if (!depositable) {
      setBlured(false);
    }
  };

  useEffect(() => {
    setBlured(!depositable);
  }, [props.token.code]);

  generateQR(props.token.address);

  return (
    <Modal open={props.open} onClose={props.onClose}>
      <Paper className={classes.container}>
        {props.error ? (
          <Container>
            <DepositAddressError errorMessage={props.error} />
          </Container>
        ) : props.loading ? (
          <Grid container justifyContent="start" alignItems="start" style={{ height: "100%" }}>
            <Grid item>
              <Preloader />
            </Grid>
          </Grid>
        ) : (
          <>
            {afterSubmissionState === "normal" && (
              <Grid container>
                {/* HEADER */}
                <ModalHeader data={modalTitleData} />

                {/* INSTRUCTIONS */}
                <Paper
                  className={clsx(classes.instructions, {
                    [classes.hide]: depositable,
                    [classes.hide]: !blured,
                  })}
                  onClick={toggleVisibility}>
                  <Typography
                    style={{
                      fontWeight: 500,
                      marginRight: 20,
                      marginLeft: 20,
                      boxShadow: "none",
                    }}>
                    {instructionsMessage}
                  </Typography>
                  <Typography textAlign={"center"} style={{ fontWeight: 700 }}>
                    Click to reveal deposit address.
                  </Typography>
                </Paper>

                {/* QR CODE */}
                <Grid
                  item
                  xs={12}
                  container
                  className={clsx({
                    [classes.blured]: blured,
                  })}
                  onClick={toggleVisibility}>
                  <Grid item>
                    <img src={qrCode} alt={props.token.address} width="190" height="190" />
                  </Grid>

                  <Grid container className={classes.addressContainer}>
                    <Stack alignSelf={"center"}>
                      <Typography className={classes.addressCode}>{props.token.address}</Typography>
                      {/* COPY ADDRESS */}
                      <Button
                        disableRipple
                        onClick={copyToClipboard}
                        startIcon={<Clipboard />}
                        style={{ textTransform: "none" }}>
                        <Typography>{COPY_ADDRESS}</Typography>
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                {/* MEMO & FORMAT*/}
                <Container>
                  {props.token.memo && (
                    <Grid item>
                      <Typography
                        style={{
                          fontSize: "20px",
                          paddingBottom: "5px",
                          fontWeight: 600,
                          color: "rgba(24,32,57,.5)",
                        }}>
                        Memo
                      </Typography>
                      <OutlinedInput
                        fullWidth
                        id="outlined-input"
                        value={props.token.memo}
                        className={classes.memoStyling}
                        readOnly
                      />
                      {/* DO NOT REMOVE! NEEDED FOR LEGAL REQUIREMENTS */}
                      <Alert severity="warning" sx={{ marginTop: "5px" }}>
                        <Typography style={{ fontSize: "12px" }}>
                          Please make sure to include the memo above. Any deposits sent without the
                          memo cannot be recovered.
                        </Typography>
                      </Alert>
                    </Grid>
                  )}

                  <Grid item>
                    <Typography className={classes.fieldTitle}>Format</Typography>
                    <OutlinedInput
                      fullWidth
                      id="outlined-input"
                      value={props.token.format}
                      className={classes.memoStyling}
                      readOnly
                    />
                    {/* ONLY FOR NON FIREBASE TOKENS */}
                    {!depositable && (
                      <Grid item xs={12}>
                        <Formik
                          initialValues={{ txId: "", amount: "" }}
                          onSubmit={submitDeposit}
                          validationSchema={validationSchema}>
                          <Form>
                            <Typography mt={2} className={classes.fieldTitle}>
                              Transaction ID
                            </Typography>
                            <Field
                              fullWidth
                              as={OutlinedInput}
                              name="txId"
                              variant="standard"
                              placeholder="Please enter the Deposit Transaction ID"
                            />
                            <Typography mt={2} className={classes.fieldTitle}>
                              Amount
                            </Typography>
                            <Field
                              fullWidth
                              as={OutlinedInput}
                              name="amount"
                              type="number"
                              placeholder="Enter the unit amount deposited"
                            />
                            <ErrorMessage name="test" />
                            <Grid
                              onClick={() => trackEvent("Select_Wallets_Page_Deposit_Submit")}
                              item
                              xs={12}
                              mt={2}
                              justifyContent={"center"}>
                              <SubmitButton
                                fullWidth
                                style={{
                                  height: "50px",
                                  marginTop: "8px",
                                }}
                              />
                            </Grid>
                          </Form>
                        </Formik>
                      </Grid>
                    )}
                    {/* DO NOT REMOVE! NEEDED FOR LEGAL REQUIREMENTS */}
                    <Typography style={{ fontSize: "12px" }} my={3}>
                      {`Please make sure to send your deposit in the format of:
                      ${props.token.format}. Any deposits not sent in this format cannot be
                      recovered.`}
                    </Typography>
                    <DepositMinimumDisclaimer tokenCode={props.token.code} />
                  </Grid>
                </Container>
              </Grid>
            )}
            {afterSubmissionState !== "normal" && (
              <Grid container justifyContent="center" style={{ height: "500px", zIndex: 10000 }}>
                <GenericMessageModal customMessage={message} hideButton halfSize />
              </Grid>
            )}
            {copy && <CopyAddress open={copy} />}
          </>
        )}
      </Paper>
    </Modal>
  );
};

export default QRModalDesktop;
